import { Button, Tooltip } from "@resource/atlas";
import { atlasClose } from "@resource/atlas/icons";
import { strings } from "@resource/common";
import clsx from "clsx";
import { ReactElement, ReactNode } from "react";
import usePrevious from "react-hooks/usePrevious";

import styles from "./BulkActionBar.module.scss";

const DEFAULT_BULK_ACTION_BAR_PROPS = {
  itemDisplayText: "item",
} as const;

const CLOSE_ICON = atlasClose;

export interface BulkActionBarProps {
  isVisible: boolean;
  isLoading: boolean;
  selectedCount: number;
  totalCount: number;
  itemDisplayText?: string;
  onSelectAll: () => void;
  onClearAll: () => void;
  onClose: () => void;
  children: ReactNode;
}

function BulkActionBar({
  itemDisplayText = DEFAULT_BULK_ACTION_BAR_PROPS.itemDisplayText,
  isVisible,
  isLoading,
  selectedCount,
  totalCount,
  onSelectAll,
  onClearAll,
  onClose,
  children,
}: BulkActionBarProps): ReactElement {
  const allSelected = selectedCount === totalCount;
  const previousAllSelected = usePrevious(allSelected);
  const allSelectedDisplay =
    selectedCount === 0 ? previousAllSelected : allSelected;
  const selectActionButtonText = allSelectedDisplay
    ? "Clear all"
    : "Select All";

  const handleToggleAllClick = () =>
    allSelected ? onClearAll() : onSelectAll();

  const closeTooltip = "Close and clear selection";
  const previousSelectedCount = usePrevious(selectedCount);
  const selectedCountDisplay =
    selectedCount === 0 ? previousSelectedCount ?? 0 : selectedCount;

  return (
    <div
      className={clsx(
        styles.shadow,
        "flex items-center fixed rounded left-1/2 transform -translate-x-1/2 bottom-8 pl-6 pr-4 py-4 bg-white focus:outline-none transition",
        {
          "translate-y-0 opacity-100": isVisible,
          "translate-y-20 opacity-0": !isVisible,
        }
      )}
    >
      <span className="whitespace-nowrap text-body-md-heavy">
        {selectedCountDisplay}&nbsp;
      </span>
      <span className="whitespace-nowrap mr-4 text-body-md">
        of {totalCount}{" "}
        {strings.pluralize(itemDisplayText, selectedCountDisplay, false)}{" "}
        selected
      </span>
      <div className="flex space-x-4">
        <Button
          size="small"
          onClick={handleToggleAllClick}
          disabled={isLoading}
        >
          {selectActionButtonText}
        </Button>
        {children}
      </div>
      <Tooltip content={closeTooltip}>
        <Button
          aria-label={closeTooltip}
          className="ml-6"
          size="small"
          isGhost
          icon={CLOSE_ICON}
          onClick={onClose}
        />
      </Tooltip>
    </div>
  );
}

export default BulkActionBar;
